import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Slide,
  Button,
  Paper,
  Link,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import usePerformanceStatus from '../hooks/usePerformanceStatus';
const PerformanceStatusBar = ({ 
  pollInterval = 5 * 60 * 1000
}) => {
  const { t } = useTranslation();
  const { status } = usePerformanceStatus(pollInterval);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasShownInSession = () => {
    return sessionStorage.getItem('performanceStatusShown') === 'true';
  };

  // Mark popup as shown in this session
  const markAsShownInSession = () => {
    sessionStorage.setItem('performanceStatusShown', 'true');
  };

  // Show/hide logic - only show if degraded is true, has message, not dismissed, and not shown in this session
  useEffect(() => {
    if (status?.degraded && status?.message && !isDismissed && !hasShownInSession()) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [status, isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    markAsShownInSession(); // Mark as shown only when user clicks close
  };

  if (!isVisible || isDismissed || !status?.message) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px' : '40px',
        pointerEvents: isVisible ? 'auto' : 'none'
      }}
    >
      <Slide direction="down" in={isVisible} mountOnEnter unmountOnExit>
        <Box
          sx={{
            width: isMobile ? '100%' : '400px',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
        <Paper
          elevation={8}
          sx={{
            background: '#FFFFFF',
            border: '1px solid #E0E0E0',
            borderRadius: '12px',
            padding: 0,
            overflow: 'hidden',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.15)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '12px 16px' : '16px 20px',
              borderBottom: '1px solid #E0E0E0',
              background: '#F8F9FA'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon 
                sx={{ 
                  color: '#FFA726', 
                  fontSize: isMobile ? 18 : 20 
                }} 
              />
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#333333',
                  fontWeight: 600,
                  fontSize: isMobile ? '13px' : '14px',
                  letterSpacing: '0.5px'
                }}
              >
                {t('performanceStatus.title')}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ padding: isMobile ? '16px' : '20px' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#333333',
                fontSize: isMobile ? '13px' : '14px',
                lineHeight: 1.5,
                marginBottom: status.statusLink ? '16px' : '16px',
                wordBreak: 'break-word',
                '& br': {
                  display: 'block',
                  margin: '8px 0',
                  content: '""'
                }
              }}
              dangerouslySetInnerHTML={{ __html: status.message }}
            />
            
            {status.statusLink && (
              <Box sx={{ textAlign: 'center', marginBottom: '16px' }}>
                <Link
                  href={status.statusLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#0088F5',
                    textDecoration: 'underline',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    '&:hover': {
                      textDecoration: 'none',
                      color: '#0066CC'
                    }
                  }}
                >
                  {t('performanceStatus.checkStatus')}
                  <OpenInNewIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                </Link>
              </Box>
            )}

            {/* Close Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={handleDismiss}
                variant="contained"
                sx={{
                  backgroundColor: '#0088F5',
                  color: '#FFFFFF',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 500,
                  padding: isMobile ? '10px 20px' : '8px 24px',
                  borderRadius: 24,
                  width: '100%',
                  textTransform: 'none',
                  minHeight: isMobile ? '44px' : 'auto',
                  '&:hover': {
                    backgroundColor: '#0066CC',
                    boxShadow: '0px 2px 8px rgba(0, 136, 245, 0.3)'
                  }
                }}
              >
                {t('performanceStatus.close')}
              </Button>
            </Box>
          </Box>
        </Paper>
        </Box>
      </Slide>
    </Box>
  );
};

export default PerformanceStatusBar;